import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, Plus } from 'lucide-react';
import { aaiApi } from '../../services/aaiApi';
import { useParticipant } from '../../hooks/useParticipant';
import type { RegistryRow } from '../../types/aai';
import { Loading, ErrorNote } from './components';
import { Card, MonoLabel, RiskPill, StatusPill, PageHeader } from './dashboards/ui';

// "Use Cases" (design §6.4) — what AI is approved to do in this department, and
// what's blocked. Reads the use-case registry and scopes it to the dept head's
// own department. Proposing a new one routes through the request/approval flow —
// the champion classifies risk + human-in-loop before it goes live.
const DeptUseCasesPage: React.FC = () => {
  const navigate = useNavigate();
  const { context, ready } = useParticipant();
  const dept = context?.department ?? null;
  const [rows, setRows] = useState<RegistryRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!ready) return;
    (async () => {
      try {
        const entries = (await aaiApi.getRegistry()).data.entries || [];
        // Scope to the dept head's own department (design framing is dept-local).
        const scoped = dept
          ? entries.filter((e) => (e.department || '').toLowerCase() === dept.toLowerCase())
          : entries;
        setRows(scoped);
      } catch {
        setError('Could not load your department use cases.');
      } finally {
        setLoading(false);
      }
    })();
  }, [ready, dept]);

  return (
    <div className="max-w-4xl mx-auto">
      <PageHeader
        title={`Use cases${dept ? ` in ${dept}` : ''}`}
        subtitle="What AI is approved to do in your department — and what's blocked. Propose new ones for the champion to classify."
      />

      {loading ? (
        <Loading label="Loading use cases…" />
      ) : error ? (
        <ErrorNote message={error} />
      ) : (
        <div className="space-y-4">
          <Card>
            <MonoLabel className="mb-3">Registered use cases</MonoLabel>
            {rows.length === 0 ? (
              <p className="text-[13px] text-gray-400">None registered yet — propose the first one below.</p>
            ) : (
              <div className="space-y-2">
                {rows.map((r, i) => (
                  <div key={i} className="flex items-center gap-3 bg-white rounded-lg px-3.5 py-2.5 border border-gray-100">
                    <span className="w-8 h-8 rounded-lg bg-brand-50 text-brand-600 grid place-items-center shrink-0"><FileText size={15} /></span>
                    <span className="text-[13px] font-medium flex-1 min-w-0 truncate">{r.name}</span>
                    <RiskPill risk={r.risk} />
                    <StatusPill status={r.status} />
                  </div>
                ))}
              </div>
            )}
          </Card>

          <div className="bg-brand-50 rounded-xl p-5 flex items-center gap-4">
            <span className="w-10 h-10 shrink-0 rounded-xl bg-white text-brand-600 grid place-items-center"><Plus size={20} /></span>
            <div className="flex-1 min-w-0">
              <div className="font-bold text-sm mb-0.5">See a pattern worth approving?</div>
              <p className="text-[12.5px] text-gray-600 m-0 leading-relaxed">
                Propose a use case. The champion assigns its risk class and human-in-loop rule before it goes live.
              </p>
            </div>
            <button
              onClick={() => navigate('/requests')}
              className="inline-flex items-center gap-1.5 bg-brand-500 hover:bg-brand-600 text-white text-[12.5px] font-semibold px-4 py-2 rounded-full whitespace-nowrap shrink-0 transition-colors"
            >
              Propose a use case
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DeptUseCasesPage;
